import React, { useMemo, useCallback } from "react";
import { TooltipProps } from "recharts";
import { ThermalSeries, formatTime, INT_ORDER } from "../../hooks/ThermalHeatMapHooks";
import { getSubsystemsForFace } from "../../hooks/useThermalAlerts";
import { FACE_ORDER } from "../../constants/thermalConstants";
import { ThresholdConfig } from "../panels/ThermalAlertThresholds";
import ChartContent, { FACE_COLORS, PHASE_STRIP_COLORS, getFaceColor, getFaceLabel, getChartTitle } from "./ThermalHeatMapChartContent";
import ThermalSubsystemChartsGrid from "./ThermalSubsystemChartsGrid";
import { tempToHex } from "../../constants/thermalConstants";
import { ThermalControlsLayout } from "../visualization/ThermalControlsBar";
import { ExposureTiming } from "../../../../../../../CommonComponents/orbital-insights/orbital-insights-type";
import { ThermalModelConfig } from "../../../../../../../Missions/Satellites/SatelliteProfile/SatelliteDesigner/ThermalDesigner/types/ThermalConfig";
import { SUBSYSTEM_NAME_TO_OPTION_ID } from "../visualization/thermalHeatmapSubsystemConfig";

// ─── Constants ───────────────────────────────────────────────────────────────

const ALL_KEYS: string[] = [...FACE_ORDER, ...INT_ORDER];

function getDisplayLabel(key: string, selectedNode: number | null, visibleFaceCount: number): string {
  let label = getFaceLabel(key);
  if (selectedNode !== null) label += ` Node ${selectedNode}`;
  else if (visibleFaceCount > 1) label += ` (Avg)`;
  return label;
}

// ─── Props ───────────────────────────────────────────────────────────────────

export interface ThermalHeatMapChartProps {
  series: ThermalSeries;
  nodesPerFace: number;
  thresholds: ThresholdConfig[];
  maxPoints?: number;
  alerts?: Array<{ timeSec: number }>;
  selectedNode?: number | null;
  isFullscreen?: boolean;
  isCompact?: boolean;
  zoomDomain?: [number, number] | null;
  onZoomDomainChange?: (domain: [number, number] | null) => void;
  sunExposures?: ExposureTiming[];
  t0Epoch?: number | null;
  thermalModel?: ThermalModelConfig | null;
  visibleSubsystems?: string[] | null;
  showSubsystemCharts?: boolean;
}

// ─── Helper Functions ────────────────────────────────────────────────────────

// Resolve subsystem-specific thresholds or fall back to general thresholds
const getSubsystemThresholds = (
  subsystemName: string,
  thermalModel: ThermalModelConfig | null | undefined,
  fallbackThresholds: ThresholdConfig[]
) => {
  if (!thermalModel?.subsystemThresholds?.[subsystemName]) {
    return fallbackThresholds;
  }

  const subsystemThresholds = thermalModel.subsystemThresholds[subsystemName];
  const operatingLimits = subsystemThresholds.operatingLimits;

  // Use null checks instead of falsy checks to allow 0°C limits
  if (operatingLimits?.min == null && operatingLimits?.max == null) {
    return fallbackThresholds;
  }

  // Build a synthetic threshold from operatingLimits
  return [
    {
      id: `subsystem_${subsystemName}`,
      name: subsystemName,
      maxC: operatingLimits.max ?? null,
      minC: operatingLimits.min ?? null,
      enabled: true,
    },
  ];
};

// Value of one chart line at a given step, read straight from the typed-array columns.
// Undefined means no data at that step, so the point omits the key (as the row-based version did)
type ChartReader = (step: number) => number | undefined;

const toValue = (v: number): number | undefined => (Number.isNaN(v) ? undefined : v);

const makeChartReader = (series: ThermalSeries, key: string, selectedNode: number | null, nodesPerFace: number): ChartReader => {
  // Per-node key (single face, subsystem charts): the node itself
  if (key.includes("_N")) {
    const col = series.column(key);
    return col ? (i) => toValue(col[i]) : () => undefined;
  }
  // Face key with a pinned node: that node only
  if (selectedNode !== null) {
    const col = series.column(`${key}_N${selectedNode}`);
    return col ? (i) => toValue(col[i]) : () => undefined;
  }
  // Face key: average of the face's nodes that have a value at this step
  const cols = Array.from({ length: nodesPerFace }, (_, n) => series.column(`${key}_N${n + 1}`))
    .filter((c): c is Float32Array => c !== undefined);
  return (i) => {
    let sum = 0, count = 0;
    for (const col of cols) {
      const v = col[i];
      if (!Number.isNaN(v)) { sum += v; count++; }
    }
    return count > 0 ? sum / count : undefined;
  };
};

// Picks the steps to chart. Per bucket it keeps the min and max of every line, so peaks and breaches are not skipped
// by a fixed stride. Also keeps the first and last step and every alert step. Budget: about maxPoints steps in total,
// shared by all lines (more lines means fewer buckets).
const pickChartSteps = (series: ThermalSeries, readers: ChartReader[], alertTimes: number[], maxPoints: number): number[] => {
  if (series.length === 0) return [];
  const picked = new Set<number>([0, series.length - 1]);
  const bucketCount = Math.max(1, Math.floor(maxPoints / (2 + 2 * readers.length)));
  const bucketSize = Math.max(1, Math.ceil(series.length / bucketCount));

  for (let start = 0; start < series.length; start += bucketSize) {
    const end = Math.min(series.length, start + bucketSize);
    for (const read of readers) {
      let minStep = -1, maxStep = -1, minV = Infinity, maxV = -Infinity;
      for (let i = start; i < end; i++) {
        const v = read(i);
        if (v === undefined) continue;
        if (v < minV) { minV = v; minStep = i; }
        if (v > maxV) { maxV = v; maxStep = i; }
      }
      if (minStep !== -1) picked.add(minStep);
      if (maxStep !== -1) picked.add(maxStep);
    }
  }

  for (const t of alertTimes) {
    const idx = series.indexAtOrAfter(t);
    if (idx < series.length && series.time(idx) === t) picked.add(idx);
  }
  return Array.from(picked).sort((a, b) => a - b);
};

// ─── Component ───────────────────────────────────────────────────────────────

interface ThermalHeatMapChartExtraProps {
  visibleFacesSet?: string[] | null;
  chartDisplaySettings?: { showLegend: boolean; showOnlySubsystems?: boolean; showEclipsePhases?: boolean; showThresholdLines?: boolean; showSubsystemCharts?: boolean };
}

const ThermalHeatMapChart: React.FC<ThermalHeatMapChartProps & ThermalHeatMapChartExtraProps> = ({
  series,
  nodesPerFace,
  thresholds,
  isFullscreen = false,
  isCompact = false,
  visibleFacesSet: controlledVisibleFaces,
  chartDisplaySettings,
  maxPoints = 1000,
  alerts = [],
  selectedNode,
  zoomDomain: controlledZoomDomain = null,
  onZoomDomainChange,
  sunExposures = [],
  t0Epoch = null,
  thermalModel = null,
  visibleSubsystems = null,
  showSubsystemCharts = false,
}) => {
  const isSubsystemChartsMode = (showSubsystemCharts ?? false) && isFullscreen;

  const visibleFacesSet = React.useMemo(() => new Set(controlledVisibleFaces || ALL_KEYS), [controlledVisibleFaces]);
  const showLegend = !isCompact && (chartDisplaySettings?.showLegend ?? true);
  const showOnlySubsystems = chartDisplaySettings?.showOnlySubsystems ?? false;
  const showEclipsePhases = chartDisplaySettings?.showEclipsePhases ?? true;
  const showThresholdLines = chartDisplaySettings?.showThresholdLines ?? true;

  // Use controlled zoom domain from parent (or local null if parent doesn't provide)
  const zoomDomain = controlledZoomDomain;

  // Precompute subsystems per face (static mapping, no re-computation needed)
  const subsystemsByFace = useMemo(() => {
    const result: Record<string, ReturnType<typeof getSubsystemsForFace>> = {};
    for (const faceKey of ALL_KEYS) {
      result[faceKey] = getSubsystemsForFace(faceKey);
    }
    return result;
  }, []);

  // Build node->subsystemName lookup for each face (reusable for future subsystems-only filter)
  const subsystemNameByFaceNode = useMemo(() => {
    const result: Record<string, Record<number, string>> = {};
    for (const faceKey of ALL_KEYS) {
      result[faceKey] = {};
      const subsystems = subsystemsByFace[faceKey] || [];
      for (const s of subsystems) {
        result[faceKey][s.nodeIndex] = s.subsystemName;
      }
    }
    return result;
  }, [subsystemsByFace]);

  const isSingleFace = visibleFacesSet.size === 1;
  const visibleFaceKeys = useMemo(() => {
    const keys = Array.from(visibleFacesSet);
    if (selectedNode !== null) {
      // Pinned node mode: just return the face keys as-is
      return keys;
    }
    if (isSingleFace) {
      const faceKey = keys[0];
      const nodeKeys: string[] = [];
      if (showOnlySubsystems) {
        // Filter to subsystem-bearing nodes only
        const subsystemNodeIndices = Object.keys(subsystemNameByFaceNode[faceKey] || {}).map(Number);
        for (const n of subsystemNodeIndices) {
          nodeKeys.push(`${faceKey}_N${n}`);
        }
      } else {
        // Show all nodes for the selected face
        for (let n = 1; n <= nodesPerFace; n++) {
          nodeKeys.push(`${faceKey}_N${n}`);
        }
      }
      return nodeKeys;
    }
    if (showOnlySubsystems) {
      // Multi-face subsystems mode: return per-node keys across all visible faces with subsystems
      const nodeKeys: string[] = [];
      for (const faceKey of keys) {
        const subsystemNodeIndices = Object.keys(subsystemNameByFaceNode[faceKey] || {}).map(Number);
        for (const n of subsystemNodeIndices) {
          nodeKeys.push(`${faceKey}_N${n}`);
        }
      }
      return nodeKeys;
    }
    return keys;
  }, [visibleFacesSet, isSingleFace, selectedNode, nodesPerFace, showOnlySubsystems, subsystemNameByFaceNode]);

  const subsystemChartGroups = useMemo(() => {
    if (!isSubsystemChartsMode) return [];

    const result: Array<{ nodeKey: string; subsystemName: string; faceLabel: string }> = [];

    for (const faceKey of ALL_KEYS) {
      if (!visibleFacesSet.has(faceKey)) continue;

      const faceLabel = getFaceLabel(faceKey);
      const subsystems = subsystemsByFace[faceKey] || [];

      for (const subsystem of subsystems) {
        const optionId = SUBSYSTEM_NAME_TO_OPTION_ID[subsystem.subsystemName];
        if (optionId && (visibleSubsystems ?? []).includes(optionId)) {
          result.push({
            nodeKey: subsystem.nodeId,
            subsystemName: subsystem.subsystemName,
            faceLabel,
          });
        }
      }
    }

    return result;
  }, [isSubsystemChartsMode, visibleFacesSet, subsystemsByFace, visibleSubsystems]);


  // The lines drawn: one per subsystem node in subsystem-grid mode, otherwise the visible face or node keys
  const seriesKeys = useMemo(
    () => (isSubsystemChartsMode ? subsystemChartGroups.map((g) => g.nodeKey) : visibleFaceKeys),
    [isSubsystemChartsMode, subsystemChartGroups, visibleFaceKeys]
  );

  // ── Chart data: ~maxPoints steps read from the series columns, always including alert steps ──
  const chartData = useMemo(() => {
    const readers = seriesKeys.map((key) => makeChartReader(series, key, selectedNode ?? null, nodesPerFace));
    const alertTimes = (alerts ?? []).map((a) => a.timeSec);
    return pickChartSteps(series, readers, alertTimes, maxPoints).map((step) => {
      const point: Record<string, number> = { time: series.time(step) };
      seriesKeys.forEach((key, k) => {
        const v = readers[k](step);
        if (v !== undefined) point[key] = v;
      });
      return point;
    });
  }, [series, seriesKeys, nodesPerFace, alerts, maxPoints, selectedNode]);

  // Filter chart data based on zoom domain
  const displayedChartData = useMemo(
    () => (zoomDomain ? chartData.filter(pt => pt.time >= zoomDomain[0] && pt.time <= zoomDomain[1]) : chartData),
    [chartData, zoomDomain]
  );

  const chartTitle = useMemo(() => {
    const baseKeys = Array.from(visibleFacesSet);
    return getChartTitle(baseKeys, ALL_KEYS.length, selectedNode ?? null, isSingleFace, showOnlySubsystems);
  }, [visibleFacesSet, selectedNode, isSingleFace, showOnlySubsystems]);

  // ── Line styling for node lines (label by subsystem name if present, else generic node label) ──
  const lineStyles = useMemo(() => {
    if (selectedNode !== null) return {};

    const styles: Record<string, { color: string; label: string }> = {};
    const colorValues = Object.values(FACE_COLORS);

    if (isSingleFace) {
      // Single-face mode: label by subsystem name or generic node label
      const faceKey = Array.from(visibleFacesSet)[0];
      const nodeSubsystemNames = subsystemNameByFaceNode[faceKey] || {};

      for (let n = 1; n <= nodesPerFace; n++) {
        const key = `${faceKey}_N${n}`;
        const label = nodeSubsystemNames[n] ?? `Node ${n}`;
        const color = colorValues[(n - 1) % colorValues.length];
        styles[key] = { color, label };
      }
    } else if (showOnlySubsystems) {
      // Multi-face subsystems mode: label as "<Face> - <Subsystem>"
      const keys = Array.from(visibleFacesSet);
      for (const faceKey of keys) {
        const faceLabel = getFaceLabel(faceKey);
        const nodeSubsystemNames = subsystemNameByFaceNode[faceKey] || {};
        const subsystemNodeIndices = Object.keys(nodeSubsystemNames).map(Number);

        for (let i = 0; i < subsystemNodeIndices.length; i++) {
          const n = subsystemNodeIndices[i];
          const key = `${faceKey}_N${n}`;
          const label = `${faceLabel} - ${nodeSubsystemNames[n]}`;
          const color = colorValues[i % colorValues.length];
          styles[key] = { color, label };
        }
      }
    }

    return styles;
  }, [isSingleFace, selectedNode, visibleFacesSet, subsystemNameByFaceNode, nodesPerFace, showOnlySubsystems]);

  // Memoized helper to generate breach areas for a threshold (max or min)
  const generateBreachAreas = useMemo(
    () => (
      data: typeof chartData,
      keys: string[],
      thresholdValue: number,
      config: { color: string; stroke: string },
      isMax: boolean
    ) => {
      const areas: { x1: number; x2: number; color: string; stroke: string }[] = [];
      let start: number | null = null;
      for (const pt of data) {
        const breaching = keys.some(
          (k) => pt[k] !== undefined && (isMax ? pt[k] > thresholdValue : pt[k] < thresholdValue)
        );
        if (breaching && start === null) {
          start = pt.time;
        } else if (!breaching && start !== null) {
          areas.push({ x1: start, x2: pt.time, ...config });
          start = null;
        }
      }
      if (start !== null && data.length > 0) {
        areas.push({ x1: start, x2: data[data.length - 1].time, ...config });
      }
      return areas;
    },
    []
  );

  // ── Subsystem-specific threshold lines and breach areas ──
  const subsystemThresholdsByNode = useMemo(() => {
    if (!isSubsystemChartsMode) return {};

    const result: Record<string, Array<{ key: string; y: number; color: string; breach: 'max' | 'min' }>> = {};

    for (const item of subsystemChartGroups) {
      const resolvedThresholds = getSubsystemThresholds(item.subsystemName, thermalModel, thresholds);

      const lines: Array<{ key: string; y: number; color: string; breach: 'max' | 'min' }> = [];
      let min = Infinity, max = -Infinity;

      for (const t of resolvedThresholds) {
        if (!t.enabled) continue;
        if (t.maxC !== null) max = Math.max(max, t.maxC);
        if (t.minC !== null) min = Math.min(min, t.minC);
      }

      if (!isFinite(min) || !isFinite(max) || min === max) {
        min = 0;
        max = 100;
      }

      for (const t of resolvedThresholds) {
        if (!t.enabled) continue;
        if (t.maxC !== null) {
          const color = tempToHex(t.maxC, min, max);
          lines.push({ key: `${t.id}_max`, y: t.maxC, color, breach: 'max' });
        }
        if (t.minC !== null) {
          const color = "#89d9ff";
          lines.push({ key: `${t.id}_min`, y: t.minC, color, breach: 'min' });
        }
      }

      result[item.nodeKey] = lines;
    }

    return result;
  }, [isSubsystemChartsMode, subsystemChartGroups, thermalModel, thresholds]);

  const subsystemBreachAreasByNode = useMemo(() => {
    if (!isSubsystemChartsMode) return {};

    const result: Record<string, Array<{ x1: number; x2: number; color: string; stroke: string }>> = {};

    for (const item of subsystemChartGroups) {
      const resolvedThresholds = getSubsystemThresholds(item.subsystemName, thermalModel, thresholds);
      const areas: Array<{ x1: number; x2: number; color: string; stroke: string }> = [];

      for (const t of resolvedThresholds) {
        if (!t.enabled) continue;
        if (t.maxC !== null) {
          areas.push(...generateBreachAreas(chartData, [item.nodeKey], t.maxC, { color: "rgba(255,107,107,0.18)", stroke: "#ff6b6b" }, true));
        }
        if (t.minC !== null) {
          areas.push(...generateBreachAreas(chartData, [item.nodeKey], t.minC, { color: "rgba(107,197,255,0.18)", stroke: "#6bc5ff" }, false));
        }
      }

      result[item.nodeKey] = areas;
    }

    return result;
  }, [isSubsystemChartsMode, subsystemChartGroups, chartData, thermalModel, thresholds]);

  // ── Breach area bands (vertical shaded regions where any face crosses a threshold) ──
  const breachAreas = useMemo(() => {
    const areas: { x1: number; x2: number; color: string; stroke: string }[] = [];
    if (chartData.length === 0 || visibleFaceKeys.length === 0) return areas;

    for (const t of thresholds) {
      if (!t.enabled) continue;
      if (t.maxC !== null) {
        areas.push(...generateBreachAreas(chartData, visibleFaceKeys, t.maxC, { color: "rgba(255,107,107,0.18)", stroke: "#ff6b6b" }, true));
      }
      if (t.minC !== null) {
        areas.push(...generateBreachAreas(chartData, visibleFaceKeys, t.minC, { color: "rgba(107,197,255,0.18)", stroke: "#6bc5ff" }, false));
      }
    }
    return areas;
  }, [chartData, thresholds, visibleFaceKeys]);

  // ── Eclipse background bands (vertical shaded regions during eclipse/both exposure) ──
  const eclipseAreas = useMemo(() => {
    if (!showEclipsePhases || t0Epoch === null || sunExposures.length === 0) return [];
    const areas: { x1: number; x2: number; color: string; stroke: string }[] = [];
    for (const exposure of sunExposures) {
      if (exposure.exposureType !== "Eclipse" && exposure.exposureType !== "Both") continue;
      areas.push({
        x1: exposure.startTime - t0Epoch,
        x2: exposure.endTime - t0Epoch,
        color: "rgba(52,84,168,0.15)",
        stroke: "#3454a8",
      });
    }
    return areas;
  }, [sunExposures, t0Epoch, showEclipsePhases]);

  // ── X-axis phase strip segments (sunlit/eclipse timeline aligned with the time axis) ──
  const phaseStripSegments = useMemo(() => {
    if (!showEclipsePhases || t0Epoch === null || sunExposures.length === 0) return [];
    const segments: { x1: number; x2: number; type: "Eclipse" | "Sunlit" }[] = [];
    for (const exposure of sunExposures) {
      segments.push({
        x1: exposure.startTime - t0Epoch,
        x2: exposure.endTime - t0Epoch,
        type: exposure.exposureType === "Sunlit" ? "Sunlit" : "Eclipse",
      });
    }
    return segments;
  }, [sunExposures, t0Epoch, showEclipsePhases]);

  // ── Threshold reference lines ─────────────────────────────────────────────
  // Assign threshold line colors from the heatmap palette based on value position
  // thresholdLines now includes label and breach type for legend
  const thresholdLines = useMemo(() => {
    const lines: { key: string; y: number; color: string; label: string; breach: 'max' | 'min' }[] = [];
    // Find min/max for normalization
    let min = Infinity, max = -Infinity;
    for (const t of thresholds) {
      if (!t.enabled) continue;
      if (t.maxC !== null) max = Math.max(max, t.maxC);
      if (t.minC !== null) min = Math.min(min, t.minC);
    }
    if (!isFinite(min) || !isFinite(max) || min === max) {
      min = 0; max = 100;
    }
    for (const t of thresholds) {
      if (!t.enabled) continue;
      if (t.maxC !== null) {
        const color = tempToHex(t.maxC, min, max);
        lines.push({ key: `${t.id}_max`, y: t.maxC, color, label: t.name, breach: 'max' });
      }
      if (t.minC !== null) {
        // Use a slightly lighter blue for the lower (min) threshold reference line
        const color = "#89d9ff"; // lighter blue than the default palette
        lines.push({ key: `${t.id}_min`, y: t.minC, color, label: t.name, breach: 'min' });
      }
    }
    return lines;
  }, [thresholds]);

  // Custom tooltip
  const renderTooltip = useCallback((props: TooltipProps<number, string>) => {
    if (!props.active || !props.payload?.length) return null;
    const timeSec = (props.payload[0]?.payload as { time: number })?.time ?? 0;
    return (
      <div className="thm-chart-tooltip">
        <div className="thm-chart-tooltip-time">{formatTime(timeSec)}</div>
        {props.payload.map((item, idx) => (
          <div key={idx} className="thm-chart-tooltip-row">
            <span className="thm-chart-tooltip-dot" style={{ background: item.color as string }} />
            <span className="thm-chart-tooltip-face">
              {lineStyles[String(item.dataKey)]?.label ?? getDisplayLabel(String(item.dataKey), selectedNode ?? null, visibleFacesSet.size)}
            </span>
            <span className="thm-chart-tooltip-val">
              {(item.value as number)?.toFixed(2)} °C
            </span>
          </div>
        ))}
      </div>
    );
  }, [selectedNode, visibleFacesSet.size, lineStyles]);

  // Render legend items
  const renderLegend = useCallback(() => (
    <div className="thm-chart-legend-container">
      <div className="thm-chart-legend-row">
        {visibleFaceKeys.map((key) => (
          <span key={key} className="thm-chart-legend-item">
            <span className="thm-chart-legend-swatch" style={{ background: lineStyles[key]?.color ?? getFaceColor(key) }} />
            <span className="thm-chart-legend-label">
              {lineStyles[key]?.label ?? getDisplayLabel(key, selectedNode ?? null, visibleFacesSet.size)}
            </span>
          </span>
        ))}
        {thresholdLines.map((tl) => (
          <span key={tl.key} className="thm-chart-legend-item">
            <span className="thm-chart-legend-swatch" style={{ background: tl.color, border: "1px dashed #888" }} />
            <span className="thm-chart-legend-label">{tl.label} ({tl.breach === 'max' ? 'Max' : 'Min'})</span>
          </span>
        ))}
      </div>
    </div>
  ), [visibleFaceKeys, lineStyles, selectedNode, visibleFacesSet.size, thresholdLines, phaseStripSegments]);

  // ─────────────────────────────────────────────────────────────────────────

  const chartUI = useMemo(
    () => (
      <>
        {isSubsystemChartsMode ? (
          <ThermalSubsystemChartsGrid
            items={subsystemChartGroups}
            chartData={displayedChartData}
            thresholdLines={showThresholdLines ? thresholdLines : []}
            breachAreas={breachAreas}
            eclipseAreas={eclipseAreas}
            phaseStripSegments={phaseStripSegments}
            renderTooltip={renderTooltip}
            onZoomSelect={isCompact ? undefined : onZoomDomainChange}
            isFullscreen={isFullscreen}
            subsystemThresholds={showThresholdLines ? subsystemThresholdsByNode : {}}
            subsystemBreachAreas={subsystemBreachAreasByNode}
          />
        ) : (
          <>
            {visibleFacesSet.size > 0 ? (
              <>
                <div className="thm-chart-title-row">
                  <div className="thm-chart-title">{chartTitle}</div>
                </div>
                <div className="thm-chart-area">
                  <ChartContent
                    chartData={displayedChartData}
                    visibleFaceKeys={visibleFaceKeys}
                    thresholdLines={showThresholdLines ? thresholdLines : []}
                    breachAreas={breachAreas}
                    eclipseAreas={eclipseAreas}
                    phaseStripSegments={phaseStripSegments}
                    renderTooltip={renderTooltip}
                    onZoomSelect={isCompact ? undefined : onZoomDomainChange}
                    lineStyles={lineStyles}
                    isFullscreen={isFullscreen}
                  />
                </div>
                {showLegend && renderLegend()}
              </>
            ) : (
              <div className="thm-chart-empty">
                Select a face or deck to display the chart
              </div>
            )}
          </>
        )}
      </>
    ),
    [isSubsystemChartsMode, subsystemChartGroups, displayedChartData, thresholdLines, breachAreas, eclipseAreas, phaseStripSegments, renderTooltip, onZoomDomainChange, isCompact, isFullscreen, visibleFacesSet.size, visibleFaceKeys, chartTitle, showLegend, renderLegend, lineStyles, subsystemThresholdsByNode, subsystemBreachAreasByNode, showThresholdLines]
  );

  return (
    <div className="thm-chart-view">
      <ThermalControlsLayout
        isFullscreen={isFullscreen}
        controls={null}
      >
        {chartUI}
      </ThermalControlsLayout>
    </div>
  );
};

export default React.memo(ThermalHeatMapChart);
